/**
 * ─── Sync Store ─────────────────────────────────────────────────────
 *
 * Global filter state (year, month) and sync metadata.
 * All page-level stores read selectedYear / selectedMonth from here.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { fetchSyncStatus, type SyncStatusResponse } from '@/api/backend'

export type YearFilter = 'all' | '2025' | '2026'
export type MonthFilter = 'all' | '01' | '02' | '03' | '04' | '05' | '06' | '07' | '08' | '09' | '10' | '11' | '12'

export const useSyncStore = defineStore('sync', () => {
  const selectedYear = ref<YearFilter>('all')
  const selectedMonth = ref<MonthFilter>('all')
  const lastSyncedAt = ref<string | null>(null)
  const syncStatus = ref<SyncStatusResponse | null>(null)
  const syncLoading = ref(false)

  /** Query params derived from current filter */
  const filterParams = computed(() => {
    const p: Record<string, string> = {}
    if (selectedYear.value !== 'all') p.year = selectedYear.value
    if (selectedMonth.value !== 'all') p.month = selectedMonth.value
    return p
  })

  function setYear(year: YearFilter) {
    selectedYear.value = year
  }

  function setMonth(month: MonthFilter) {
    selectedMonth.value = month
  }

  /** Update lastSyncedAt from any API response meta */
  function updateMeta(meta?: { last_synced_at?: string | null }) {
    if (meta?.last_synced_at) {
      lastSyncedAt.value = meta.last_synced_at
    }
  }

  /** Fetch sync status from backend */
  async function loadSyncStatus() {
    syncLoading.value = true
    try {
      const data = await fetchSyncStatus()
      syncStatus.value = data
      if (data.last_sync?.completed_at) {
        lastSyncedAt.value = data.last_sync.completed_at
      }
      updateMeta(data.meta)
    } catch {
      // ignore
    } finally {
      syncLoading.value = false
    }
  }

  return {
    selectedYear,
    selectedMonth,
    lastSyncedAt,
    syncStatus,
    syncLoading,
    filterParams,
    setYear,
    setMonth,
    updateMeta,
    loadSyncStatus,
  }
})
